<template>
  <Wrapper class="q-px-lg q-py-xl">
    <h5 class="q-mb-xl"><q-icon name="person" size="lg" color="primary" /> Shipper Management</h5>

    <ShipperFilter
      :search.sync="search"
      :sort.sync="sort"
      :verification.sync="verification"
      @reset="handleReset"
      @search="handleSearch"
    />

    <LoadingTable v-if="isLoading" :columns="columns" />
    <DataTable v-else ref="table" :columns="columns">
      <tr v-if="!users.length">
        <td class="text-center" :colspan="columns.length">No records found.</td>
      </tr>

      <tr v-for="user in users" :key="user.id">
        <td>
          <span class="action" @click="openEditUserDialog(user)">
            {{ user.id | formatId }}
          </span>
        </td>

        <td class="text-capitalize">{{ user.name }}</td>

        <td>
          <avatar :src="user.profile.companyLogo" />
          <span class="text-uppercase">{{ user.businessName }}</span>
        </td>

        <td>{{ user.email }}</td>

        <td>{{ user.createdAt | formatDateTime }}</td>

        <td>{{ user.phone }}</td>

        <td class="text-weight-medium">
          <span v-if="user.isActive === true" class="text-positive">Active</span>
          <span v-else class="text-negative">Suspended</span>
        </td>

        <td class="text-weight-medium">
          <template v-if="user.verification" class="text-weight-medium">
            <span v-if="user.verification.status === 'rejected'" class="text-negative"> Rejected </span>
            <span v-else-if="user.verification.status === 'pending'" class="text-warning"> Pending </span>
            <span v-else-if="user.verification.status === 'verified'" class="text-positive"> Verified </span>
          </template>

          <span v-else class="text-muted">Unverified</span>
        </td>

        <td v-if="user.profile && user.profile.consent">Yes</td>
        <td v-else>No</td>

        <td>
          <span v-if="user.isActive === true" class="action" @click="suspendUser = user"> Suspend user </span>
          <span v-else class="action" @click="enableUser = user">Enable user</span>
        </td>
      </tr>
    </DataTable>

    <Pagination v-show="totalPages > 1" :page.sync="currentPage" :total="totalPages" />

    <q-dialog v-if="!!selectedUser" :value="!!selectedUser" @hide="onCloseEditUserDialog">
      <q-card style="width: 50rem; max-width: 80vw">
        <q-card-section>
          <h6 class="text-center text-primary">Shipper Management Detail</h6>
        </q-card-section>

        <!-- Account and status -->
        <q-card-section>
          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-6">
              <p class="text-body1 text-weight-medium">
                Registered account:
                <span class="text-body2">{{ selectedUser.email }}</span>
              </p>
            </div>

            <div class="col-12 col-md-6">
              <p class="text-body1 text-weight-medium">
                Status:
                <template v-if="selectedUser.isActive === true">
                  <span class="text-positive">Active </span>
                  <span class="action" @click="suspendUser = selectedUser">Suspend user</span>
                </template>

                <template v-else>
                  <span class="text-negative">Suspended </span>
                  <span class="action" @click="enableUser = selectedUser"> Enable user </span>
                </template>
              </p>
            </div>
          </div>
        </q-card-section>

        <!-- Info -->

        <q-card-section>
          <h6>Business Details:</h6>

          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-6 flex items-center">
              <div class="q-gutter-x-lg">
                <span class="text-muted">Portrait:</span>
                <q-avatar size="5rem">
                  <img alt="user avatar" :src="editUserImage" />
                </q-avatar>
              </div>
              <div class="self-end">
                <q-btn flat round color="primary" icon="cloud_upload" @click="() => $refs.picture.click()" />
                <input
                  ref="picture"
                  type="file"
                  hidden
                  name="logo"
                  accept="image/jpeg, image/png"
                  @change="handleFileChange"
                />
              </div>
            </div>

            <div class="md-layout-item">
              <q-input readonly label="Registered Account ID" :value="selectedUser.id" />

              <template v-if="selectedUser.verification">
                <q-select
                  v-model="selectedUser.verification.status"
                  emit-value
                  map-options
                  label="Verify Status"
                  :options="verifyOptions"
                />

                <q-btn
                  flat
                  class="full-width"
                  color="primary"
                  label="confirm"
                  :disabled="selectedUser.verification.status === 'pending'"
                  @click="debouncedVerifyUser(selectedUser.verification.status)"
                />
              </template>

              <q-select v-else readonly value="" label="User is unverified" />
            </div>
          </div>
        </q-card-section>

        <!-- Business details -->
        <q-card-section>
          <template v-if="selectedUser.verification">
            <p
              class="cursor-pointer text-primary text-right q-mt-md q-mb-none"
              @click="isVerificationOpen = !isVerificationOpen"
            >
              View user verification profile
            </p>

            <ShipperVerificationInfo
              :open="isVerificationOpen"
              :verification="selectedUser.verification"
              :files="files"
            />
          </template>

          <div class="q-pa-md bg-gray">
            <div class="row q-col-gutter-md">
              <div class="col-12 col-md-6">
                <q-input
                  v-model="selectedUser.businessName"
                  no-error-icon
                  label="Business Name"
                  error-message="Business name is required"
                  :error="$v.selectedUser.businessName.$error"
                />
              </div>

              <div class="col-12 col-md-6">
                <q-select
                  v-model="selectedUser.workingPosition"
                  no-error-icon
                  map-options
                  emit-value
                  label="Business description"
                  error-message="This field is required"
                  :error="$v.selectedUser.workingPosition.$error"
                  :options="positions"
                />
              </div>

              <div class="col-12 col-md-6">
                <q-input
                  v-model="selectedUser.name"
                  no-error-icon
                  label="Contact Person"
                  error-message="Contact person is required"
                  :error="$v.selectedUser.name.$error"
                />
              </div>

              <div class="col-12 col-md-6">
                <q-input v-model="selectedUser.phone" no-error-icon label="Phone" :error="$v.selectedUser.phone.$error">
                  <template #error>
                    <span v-if="!$v.selectedUser.phone.required">Phone is required</span>
                    <span v-else-if="!$v.selectedUser.phone.numeric"> Phone must be numeric value </span>
                  </template>
                </q-input>
              </div>

              <div class="col-12 col-md-6">
                <q-input
                  v-for="(address, idx) in selectedUser.profile.companyAddress"
                  :key="idx"
                  v-model="address.v"
                  no-error-icon
                  label="Company address"
                  error-message="Address is required"
                  :error="$v.selectedUser.profile.companyAddress.$each[idx].v.$error"
                >
                  <template v-if="idx !== 0" #append>
                    <q-btn
                      flat
                      round
                      dense
                      color="muted"
                      icon="remove"
                      @click.stop="selectedUser.profile.companyAddress.splice(idx, 1)"
                    />
                  </template>
                </q-input>
                <div class="flex justify-end">
                  <q-btn
                    v-if="selectedUser.profile.companyAddress.length < 4"
                    :ripple="false"
                    flat
                    no-caps
                    dense
                    color="primary"
                    label="add address"
                    icon="add"
                    @click="selectedUser.profile.companyAddress.push({ v: '' })"
                  />
                </div>
              </div>

              <div class="col-12 col-md-6">
                <q-input v-model="deliveryAddress" label="Delivery address" disable />
              </div>

              <div class="col-12 col-md-6">
                <q-input v-model="selectedUser.profile.mailingAddress" label="Mailing address" disable> </q-input>
              </div>

              <div class="col-12 col-md-6">
                <q-input :value="verifiedPhone" label="Verified Mobile Phone" disable />
              </div>
            </div>
          </div>
        </q-card-section>

        <!-- Consent -->
        <q-card-section>
          <div class="q-pa-md bg-gray">
            <div class="row q-col-gutter-md">
              <div class="col">
                <q-checkbox disabled label="Marketing Consent" :value="selectedUser.profile.consent" />
              </div>
              <div class="col">
                <q-checkbox disable label="Verified Email" :value="isVerifiedEmail" />
              </div>
              <div class="col">
                <q-checkbox v-model="isVerifiedPhone" label="Verified Phone" disable />
              </div>
            </div>
          </div>
        </q-card-section>

        <!-- Notes -->
        <q-card-section>
          <h6>Admin Notes:</h6>
          <notes v-if="selectedUser" :user-id="selectedUser.id" />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat color="muted" label="Cancel" />
          <q-btn flat color="primary" label="Save" @click="saveUser(false)" />
          <q-btn flat color="primary" label="Save and Exit" @click="isSaveDialogOpen = true" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="isSaveDialogOpen">
      <q-card>
        <q-card-section>
          <h6 class="text-primary">Save Confirm</h6>
        </q-card-section>

        <q-card-section>
          <p>Are you sure you want to update this shipper information?</p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="cancel" />
          <q-btn color="primary" label="Save" @click="saveUser" />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <!--Confirm upload image-->
    <q-dialog :value="!!tempFile" persistent>
      <q-card>
        <q-card-section>
          <h6 class="text-primary">Upload Confirm</h6>
          <p>Are you sure you want to upload this photo?</p>
          <q-avatar size="5rem">
            <img alt="user avatar" :src="imagePreview" />
          </q-avatar>
          <sub class="text-muted block">Size Limit: 100*100px</sub>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="cancel" @click="resetPictureInput" />
          <q-btn color="primary" label="Save" @click="changePhoto" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <ModifyUserStatusDialog
      title="Suspend user"
      :user.sync="suspendUser"
      :on-confirm="() => changeUserStatus('suspend')"
    >
      <p v-if="!!suspendUser">
        Are you sure you want to suspend
        <span class="text-weight-medium">{{ suspendUser.name }}</span
        >?
      </p>
      <p>This user won't be able to make new requests or check their ongoing requests.</p>
    </ModifyUserStatusDialog>

    <ModifyUserStatusDialog title="Enable user" :user.sync="enableUser" :on-confirm="() => changeUserStatus('enable')">
      <p>Enable this user?</p>
      <div v-if="!!enableUser" class="text-center">
        <p>
          User name:
          <span class="text-weight-bold text-capitalize">{{ enableUser.name }}</span>
        </p>
        <p>
          Business name:
          <span class="text-weight-bold">{{ enableUser.businessName }}</span>
        </p>
      </div>
    </ModifyUserStatusDialog>
  </Wrapper>
</template>

<script>
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { required, requiredIf, numeric } from 'vuelidate/lib/validators';
import Wrapper from '@/components/Wrapper.vue';
import DataTable from '@/components/DataTableNew.vue';
import LoadingTable from '@/components/LoadingTable.vue';
import Pagination from '@/components/Pagination.vue';
import { getErrorMessage, getSourceFromAsset } from '@/utils';
import formatDateMixin from '@/utils/mixins/formatDateMixin';
import API from '@/apis';
import uploadFile from '@/utils/uploadFile';
import Avatar from '@/components/Avatar.vue';
import ModifyUserStatusDialog from './Shipper/ModifyUserStatusDialog.vue';
import ShipperFilter from './Shipper/ShipperFilter.vue';
import Notes from './Transporter/Notes.vue';

export default {
  components: {
    LoadingTable,
    Avatar,
    Notes,
    Wrapper,
    DataTable,
    Pagination,
    ShipperFilter,
    ModifyUserStatusDialog,
    ShipperVerificationInfo: () => import('./Shipper/ShipperVerificationInfo.vue'),
  },

  mixins: [formatDateMixin],

  data() {
    return {
      isLoading: true,
      users: [],
      search: '',
      sort: '',
      verification: '',
      currentPage: 1,
      totalPages: 1,
      isSaveDialogOpen: false,
      isVerificationOpen: false,
      selectedUser: null,
      suspendUser: null,
      enableUser: null,
      tempFile: null,
      columns: [
        'ID',
        'Contact Person',
        'Company Name',
        'Email',
        'Member Since',
        'Phone',
        'Status',
        'Verification Status',
        'Marketing Consent',
        'Actions',
      ],
      positions: [
        { label: 'Importer/Exporter', value: 'importer' },
        { label: 'Trading Company', value: 'trader' },
        { label: 'Freight Forwarder', value: 'forwarder' },
        { label: 'Customs Broker', value: 'broker' },
        { label: 'Individual', value: 'individual' },
      ],
      verifyOptions: [
        { label: 'Rejected', value: 'rejected' },
        { label: 'Pending', value: 'pending', disable: true },
        { label: 'Verify', value: 'verified' },
      ],
      files: {
        license: {},
        document: {},
      },
    };
  },

  computed: {
    imagePreview() {
      return this.tempFile ? URL.createObjectURL(this.tempFile) : null;
    },
    editUserImage() {
      return this.selectedUser.profile?.companyLogo
        ? this.selectedUser.profile.companyLogo
        : getSourceFromAsset('icon_Personal.png');
    },
    verifiedPhone() {
      return get(this.selectedUser, 'verifiedPhone.number', '');
    },
    isVerifiedPhone() {
      return get(this.selectedUser, 'verifiedPhone.isVerified', false);
    },
    isVerifiedEmail() {
      return get(this.selectedUser, 'verifiedEmail', false);
    },
    deliveryAddress() {
      const address = get(this.selectedUser, 'profile.deliveryAddress', null);
      if (!address) {
        return '';
      }

      return `${address.street}, ${address.suburb}, ${address.city}, ${address.postCode}`;
    },
  },

  watch: {
    currentPage() {
      this.debouncedGetShippers();
      window.scrollTo({ top: this.$refs.table.$el.offsetTop, behavior: 'smooth' });
    },
  },

  created() {
    this.debouncedVerifyUser = debounce(this.verifyUser, 500);
    this.debouncedGetShippers = debounce(this.getShippers, 500);

    this.getShippers();
  },

  methods: {
    getSourceFromAsset,
    resetPictureInput() {
      this.$refs.picture.value = null;
      this.tempFile = null;
    },
    handleFileChange(event) {
      const [file] = event.target.files;
      this.tempFile = file;
    },
    handleSearch() {
      if (this.currentPage > 1) {
        // Reset page on search
        // new page value will trigger watch
        this.currentPage = 1;
        this.totalPages = 1;
      } else {
        this.debouncedGetShippers();
      }
    },
    async handleReset() {
      // Do nothing at initial state
      if (this.currentPage === 1 && this.totalPages === 1 && !this.search && !this.sort && !this.verification) {
        return;
      }

      if (this.currentPage === 1) await this.debouncedGetShippers();

      this.search = '';
      this.sort = '';
      this.verification = '';
      // new page value will trigger watch
      this.currentPage = 1;
      this.totalPages = 1;
    },
    getParams() {
      const params = {};

      if (this.search) params.search = this.search;
      if (this.sort) params.sort = this.sort;
      if (this.verification) params.verification = this.verification;
      if (this.currentPage > 1) params.page = this.currentPage;

      return params;
    },
    async getShippers() {
      this.isLoading = true;
      try {
        const params = this.getParams();
        const {
          data: { data, pagination },
        } = await this.$axios.request(API.admin.getShippers({ params }));

        this.users = data;
        this.currentPage = pagination.page;
        this.totalPages = pagination.pages;
      } catch (error) {
        this.$q.notify({ message: getErrorMessage(error), type: 'negative' });
      } finally {
        this.isLoading = false;
      }
    },
    debouncedGetShippers() {},
    async openEditUserDialog(user) {
      this.selectedUser = {
        ...user,
        profile: {
          ...user.profile,
          companyAddress: user.profile?.companyAddress?.length
            ? user.profile.companyAddress.map((c) => ({ v: c }))
            : [{ v: '' }],
        },
      };
      this.files.license = await this.getFileInfo(user.verification?.license);
      this.files.document = await this.getFileInfo(user.verification?.document);
    },
    closeEditUserDialog() {
      this.isSaveDialogOpen = false;
    },
    onCloseEditUserDialog() {
      this.selectedUser = null;
      this.files.license = {};
      this.files.document = {};
    },
    async changePhoto() {
      if (!this.tempFile || !this.selectedUser.id) {
        return;
      }
      const userId = this.selectedUser.id;
      const url = await uploadFile(this.tempFile, 'image');
      try {
        await this.$axios.patch(`/admin/shipper/${userId}/company-logo`, {
          url,
        });
        this.selectedUser.profile.companyLogo = url;
        const user = this.users.find((v) => v.id === userId);
        user.profile.companyLogo = url;
        this.resetPictureInput();
        this.$q.notify({ message: 'Photo successfully updated!', type: 'positive' });
      } catch (error) {
        this.$q.notify({ message: getErrorMessage(error), type: 'negative' });
      }
    },
    async saveUser(closeDialog = true) {
      try {
        this.$v.$reset();
        this.$v.$touch();

        if (this.$v.$invalid) {
          throw new Error('Validation failed!');
        }

        this.$store.commit('START_LOADING');

        const payload = {
          ...this.selectedUser,
          profile: {
            ...this.selectedUser.profile,
            companyAddress: this.selectedUser.profile.companyAddress.filter((c) => !!c.v).map((c) => c.v),
            deliveryAddress: this.selectedUser.profile.deliveryAddress,
            mailingAddress: this.selectedUser.profile.mailingAddress,
          },
        };

        delete payload.jobsBooked;

        const { data } = await this.$axios.request(API.admin.updateShipper(payload.id, payload));

        const oItem = this.users.find((v) => v.id === data.id);
        Object.assign(oItem, data);

        this.$q.notify({ message: 'User successfully updated!', type: 'positive' });

        if (closeDialog) {
          this.closeEditUserDialog();
        }
      } catch (error) {
        this.$q.notify({ message: getErrorMessage(error), type: 'negative' });
        this.isSaveDialogOpen = false;
      } finally {
        this.$store.commit('FINISH_LOADING');
      }
    },
    async changeUserStatus(status) {
      const user = status === 'enable' ? this.enableUser : this.suspendUser;
      const active = status === 'enable';

      await this.$axios.request(API.admin.changeShipperStatus({ id: user.id, active }));

      const userIndex = this.users.findIndex((u) => u.id === user.id);

      this.users[userIndex].isActive = active;
      if (this.selectedUser) this.selectedUser.isActive = active;

      this.suspendUser = null;
      this.enableUser = null;

      this.$q.notify({ type: 'positive', message: 'User status successfully changed!' });
    },
    async verifyUser(status) {
      await this.$axios.patch(`/admin/shipper/${this.selectedUser.id}/verify`, {
        status,
      });

      this.$q.notify({
        type: 'positive',
        message: 'Successfully changed user verification status!',
      });
    },
    debouncedVerifyUser() {},
    async getFileInfo(url = '') {
      const [filename] = url.split('/').slice(-1);
      if (!filename) {
        return {};
      }
      const { data } = await this.$axios.request(API.file.size(filename));
      return data;
    },
  },

  validations: {
    selectedUser: {
      businessName: {
        required,
      },
      workingPosition: {
        required,
      },
      name: {
        required,
      },
      phone: {
        required,
        numeric,
      },
      profile: {
        companyAddress: {
          $each: {
            v: {
              required: requiredIf(function () {
                return this.selectedUser.profile.companyAddress[0].v !== '';
              }),
            },
          },
        },
        deliveryAddress: {
          $each: {
            v: {
              required: requiredIf(function () {
                return this.selectedUser.profile.deliveryAddress[0].v !== '';
              }),
            },
          },
        },
        mailingAddress: {
          required,
        },
      },
    },
  },
};
</script>
